import { Button, Popover, Tooltip } from '@mantine/core';
import { RichTextEditor } from '@mantine/tiptap';
import {
  MAIL_DEFS,
  PLACEHOLDER_GROUP_LABEL,
  PLACEHOLDERS,
  type MailKind,
  type PlaceholderGroup,
  type PlaceholderKey,
} from '@reditelji/shared';
import { useEditor, type Editor } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';
import { CtaFields } from './CtaPill';
import { mailExtensions } from './extensions';

const GROUP_ORDER: PlaceholderGroup[] = ['oseba', 'dogodek', 'ekipa', 'vsebina'];

/** The mail body editor. What it understands is `mailExtensions`. */
export function useTemplateEditor(
  body: string,
  onChange: (html: string) => void,
  docKey: string,
  templateKey: MailKind,
): Editor | null {
  const editor = useEditor({
    extensions: mailExtensions(templateKey),
    content: body,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
  });

  /*
   * Re-seed on `docKey`, never on the body text.
   *
   * The editor normalises what it is given — a block node lifted out of the
   * paragraph that held it, an attribute reordered — so the HTML it hands back
   * is rarely byte-identical to the HTML it was given. Comparing the two to
   * decide whether to re-seed therefore never settles: it re-seeds on every
   * render and the document grows a copy of anything the normaliser moved.
   *
   * `docKey` says what is a new document instead: another template, another
   * language, or a deliberate replacement (undo, restore defaults, an AI
   * translation). Typing changes none of those.
   */
  const seeded = useRef(docKey);
  useEffect(() => {
    if (!editor || seeded.current === docKey) return;
    seeded.current = docKey;
    editor.commands.setContent(body, { emitUpdate: false });
  }, [editor, docKey, body]);

  return editor;
}

/**
 * The editor with its toolbar. `onEditor` hands the instance back so a screen
 * that also carries a placeholder palette can insert into it.
 */
export function BodyEditor({
  value,
  onChange,
  templateKey,
  docKey = templateKey,
  onEditor,
}: {
  value: string;
  onChange: (html: string) => void;
  templateKey: MailKind;
  /** Changes when `value` is a different document rather than an edit of it. */
  docKey?: string;
  onEditor?: (editor: Editor | null) => void;
}) {
  const editor = useTemplateEditor(value, onChange, docKey, templateKey);
  useEffect(() => onEditor?.(editor), [editor, onEditor]);
  if (!editor) return null;
  return (
    <RichTextEditor editor={editor} className="ml-rte">
      <EditorToolbar editor={editor} templateKey={templateKey} />
      <RichTextEditor.Content />
    </RichTextEditor>
  );
}

export function EditorToolbar({
  editor,
  templateKey,
}: {
  editor: Editor;
  templateKey: MailKind;
}) {
  return (
    <RichTextEditor.Toolbar sticky stickyOffset={0}>
      <RichTextEditor.ControlsGroup>
        <RichTextEditor.Bold />
        <RichTextEditor.Italic />
      </RichTextEditor.ControlsGroup>
      <RichTextEditor.ControlsGroup>
        <RichTextEditor.H2 />
        <RichTextEditor.H3 />
      </RichTextEditor.ControlsGroup>
      <RichTextEditor.ControlsGroup>
        <RichTextEditor.BulletList />
        <RichTextEditor.OrderedList />
        <RichTextEditor.Hr />
      </RichTextEditor.ControlsGroup>
      <RichTextEditor.ControlsGroup>
        <RichTextEditor.Link />
        <RichTextEditor.Unlink />
      </RichTextEditor.ControlsGroup>
      {MAIL_DEFS[templateKey].buttons ? (
        <RichTextEditor.ControlsGroup>
          <CtaControl editor={editor} templateKey={templateKey} />
        </RichTextEditor.ControlsGroup>
      ) : null}
    </RichTextEditor.Toolbar>
  );
}

/**
 * The button block. Its link is normally ⟦povezava⟧ — the volunteer's own
 * briefing — so that is what the field starts as; a free URL is allowed for
 * anything else the mail wants to point at.
 *
 * This control only ever inserts. A button already in the letter is edited on
 * the pill itself, where its own fields live (see `CtaPill`).
 */
function CtaControl({ editor, templateKey }: { editor: Editor; templateKey: MailKind }) {
  const [opened, setOpened] = useState(false);
  const [label, setLabel] = useState('Odpri brifing');
  const canLink = MAIL_DEFS[templateKey].allows.includes('povezava');
  const [href, setHref] = useState(canLink ? '{{povezava}}' : 'https://');

  const insert = () => {
    editor.chain().focus().insertCta({ label: label.trim() || 'Odpri', href: href.trim() }).run();
    setOpened(false);
  };

  return (
    <Popover opened={opened} onChange={setOpened} position="bottom-start" withArrow width={280}>
      <Popover.Target>
        <button
          type="button"
          className="ml-toolbtn"
          onClick={() => setOpened((o) => !o)}
          title="Vstavi gumb"
        >
          Gumb
        </button>
      </Popover.Target>
      <Popover.Dropdown>
        <CtaFields
          label={label}
          href={href}
          onLabel={setLabel}
          onHref={setHref}
          allowsPovezava={canLink}
        />
        <Button size="xs" mt={10} fullWidth onClick={insert}>
          Vstavi
        </Button>
      </Popover.Dropdown>
    </Popover>
  );
}

/**
 * The placeholder palette. Only what this template can actually resolve is
 * offered — the uvodno pismo has no magic link, so it is not on the list.
 */
export function PlaceholderPalette({
  templateKey,
  onInsert,
}: {
  templateKey: MailKind;
  onInsert: (key: PlaceholderKey) => void;
}) {
  const allows = MAIL_DEFS[templateKey].allows;
  return (
    <div className="ml-palette">
      {GROUP_ORDER.map((group) => {
        const keys = allows.filter((k) => PLACEHOLDERS[k].group === group);
        if (keys.length === 0) return null;
        return (
          <div key={group}>
            <div className="ml-palette-group">{PLACEHOLDER_GROUP_LABEL[group]}</div>
            <div className="ml-palette-chips">
              {keys.map((key) => (
                <Tooltip
                  key={key}
                  label={PLACEHOLDERS[key].hint}
                  multiline
                  w={240}
                  withArrow
                  openDelay={300}
                >
                  <button
                    type="button"
                    className={`ml-chip ml-chip--${PLACEHOLDERS[key].group}${
                      PLACEHOLDERS[key].block ? ' ml-chip--paletteblock' : ''
                    }`}
                    onClick={() => onInsert(key)}
                  >
                    {key}
                  </button>
                </Tooltip>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
